import React, { useCallback } from 'react';
import { FieldHookConfig, useField } from 'formik';

import * as Types from '../types';

import Field from '../Field';

import classes from '../Field.module.scss';

/**
 * Sanitizes input to allow only digits and at most one decimal point.
 * Examples: "5", "5.3", "5.35" — valid. "5.3.4" → "5.34", "abc" → ""
 */
function sanitizeFractionalInput(value: string): string {
  if (value == null || value === '') return '';
  const str = String(value);
  let hasDot = false;
  const result: string[] = [];
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (ch >= '0' && ch <= '9') {
      result.push(ch);
    } else if (ch === '.' && !hasDot) {
      hasDot = true;
      result.push(ch);
    }
  }
  return result.join('');
}

/**
 * Checks if the value is a valid fractional number (integer or decimal).
 * Examples: "5", "5.3", "5.35" — valid. "", ".", "5." (while typing) — allowed as intermediate.
 */
export function isValidFractionalNumber(value: string): boolean {
  if (value == null || value === '') return true;
  const s = String(value).trim();
  if (s === '' || s === '.') return true;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return false;
  if (s.includes('.')) {
    return /^\d*\.?\d*$/.test(s);
  }
  return /^\d+$/.test(s);
}

const FractionalNumber: React.FC<Types.FieldProps> = ({
  placeholder,
  showError = true,
  disabled,
  readOnly,
  ...props
}) => {
  const [field, meta, helpers] = useField(props as FieldHookConfig<string>);
  const hasError = !!(showError && meta.touched && meta.error);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const sanitized = sanitizeFractionalInput(raw);
      helpers.setValue(sanitized);
    },
    [helpers]
  );

  return (
    <Field {...{ hasError, error: meta.error, disabled, readOnly, ...props }}>
      {({ onFocus, onBlur }) => (
        <input
          {...field}
          value={field.value ?? ''}
          onChange={handleChange}
          {...{ placeholder, disabled, onFocus, readOnly }}
          id={field.name}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          className={classes.input}
          onBlur={(e) => {
            field.onBlur(e);
            onBlur();
          }}
        />
      )}
    </Field>
  );
};

export default FractionalNumber;
